// src/pages/BulletinBoards.js
//
// THE BULLETIN BOARDS, AS RULING 173 DEFINES THEM (2026-09-17). Bob, this session:
//
//   "General Chairman make the posts, only the general chairman can remove them. Anyone can copy
//   digitally. LC can post from their own bulletin boards those things that matter to their specific
//   member / local (gca) members"
//   "GC full authority to post, edit, and remove from his own board and that of all bulletine boards
//   within her general committee, the lcs only authority and responsibility is limited to their own
//   posts"
//
// Every rule of who-may is src\data\bulletinBoard.js's; the Firestore rules for /bulletinPosts hold
// the same line server-side and win where the two ever disagree (ruling 16). This page draws and
// writes and decides nothing - the same split as src\pages\WarRoom.js, deliberately.
//
// A board is an address, not a document, so there is nothing to create: the boards a person can see
// come from his committee and his scopes. An officer who holds no scope sees the committee board
// only, and that is not an error state - it is what his office reaches.
import React, { useEffect, useMemo, useState } from 'react';
import UnderConstruction from '../components/UnderConstruction';   // section 542
import { db } from '../firebase';
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc } from 'firebase/firestore';
import {
  boardsForUser, mayPost, mayEditPost, mayWithdrawPost, refusalToEdit,
  composePost, editPost, withdrawPost, postsOnBoard, withdrawnOnBoard, copyText,
  TITLE_MAX, BODY_MAX,
} from '../data/bulletinBoard';
import RulingRef from '../components/RulingRef';   // ruling 168

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

function CopyButton({ post, board }) {
  const [said, setSaid] = useState('');
  // "Anyone can copy digitally" - ruling 173. This is an affordance on a notice the reader is
  // already looking at; it widens nothing. The textarea fallback is for a browser that refuses
  // the clipboard API, so the copy never silently does nothing.
  const go = async () => {
    const text = copyText(post, board);
    try {
      await navigator.clipboard.writeText(text);
      setSaid('Copied');
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      let done = false;
      try { done = document.execCommand('copy'); } catch (e2) { done = false; }
      document.body.removeChild(ta);
      setSaid(done ? 'Copied' : 'Select the text and copy it');
    }
    setTimeout(() => setSaid(''), 2500);
  };
  return (
    <button className="btn btn-secondary btn-sm" onClick={go} title="Copy this notice">
      {said || 'Copy'}
    </button>
  );
}

export function PostCard({ post, board, user, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(post.title || '');
  const [body, setBody] = useState(post.body || '');
  const [reason, setReason] = useState('');
  const [removing, setRemoving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const mayEdit = mayEditPost(user, board, post);
  const mayRemove = mayWithdrawPost(user, board, post);

  const write = async (r) => {
    setErr('');
    if (!r.ok) { setErr(r.errors.join(' ')); return; }
    setBusy(true);
    try {
      await updateDoc(doc(db, 'bulletinPosts', post.id), r.patch);
      setEditing(false); setRemoving(false);
      if (onChanged) onChanged();
    } catch (e) { setErr('Not saved: ' + (e && e.code ? e.code : e.message)); }
    setBusy(false);
  };

  return (
    <div style={{ background: '#0D1B2A', border: '1px solid ' + C.line, borderLeft: '3px solid ' + C.accent, borderRadius: 8, padding: '12px 16px', marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{post.title}</div>
        <div style={{ color: C.dim, fontSize: 11, whiteSpace: 'nowrap' }}>
          {post.postedAt ? String(post.postedAt).slice(0, 10) : 'date not recorded'}
        </div>
      </div>
      <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
        {post.postedByName}
        {post.postedByRole === 'general_chair' ? ' · General Chairperson' : (post.postedByRole === 'local_chair' ? ' · Local Chairperson' : '')}
        {post.editedAt ? ' · edited ' + String(post.editedAt).slice(0, 10) : ''}
      </div>

      {editing ? (
        <div style={{ marginTop: 10 }}>
          <input aria-label="Title of the notice" className="form-input" value={title} maxLength={TITLE_MAX} onChange={e => setTitle(e.target.value)} style={{ width: '100%', marginBottom: 6 }} />
          <textarea aria-label="The notice itself" className="form-input" value={body} maxLength={BODY_MAX} rows={6} onChange={e => setBody(e.target.value)} style={{ width: '100%' }} />
          <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => write(editPost(user, board, post, { title, body }))}>Save</button>
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => { setEditing(false); setTitle(post.title || ''); setBody(post.body || ''); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 13, color: C.text, marginTop: 8, whiteSpace: 'pre-wrap' }}>{post.body}</div>
      )}

      {removing ? (
        <div style={{ marginTop: 10, borderTop: '1px solid ' + C.line, paddingTop: 8 }}>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 6 }}>
            Removing takes it off the board. It stays in the record with your name on the removal.
            <RulingRef user={user} n={173} why="A notice taken off a board is kept, with who removed it and when, so the board's history is never silently rewritten." />
          </div>
          <input aria-label="Reason (optional)" className="form-input" placeholder="Reason (optional)" value={reason} onChange={e => setReason(e.target.value)} style={{ width: '100%', marginBottom: 6 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => write(withdrawPost(user, board, post, { reason }))}>Remove from the board</button>
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setRemoving(false)}>Cancel</button>
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
        <CopyButton post={post} board={board} />
        {mayEdit && !editing ? <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>Edit</button> : null}
        {mayRemove && !removing ? <button className="btn btn-secondary btn-sm" onClick={() => setRemoving(true)}>Remove</button> : null}
        {!mayEdit && user && (user.role === 'local_chair' || user.role === 'general_chair') ? (
          <span style={{ fontSize: 11, color: C.dim }}>{refusalToEdit(user, board, post)}</span>
        ) : null}
      </div>

      {err ? <div style={{ color: C.bad, fontSize: 12, marginTop: 6 }}>{err}</div> : null}
    </div>
  );
}

function Composer({ board, user, onPosted }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!mayPost(user, board)) return null;
  const post = async () => {
    setErr('');
    const r = composePost(user, board, { title, body });
    if (!r.ok) { setErr(r.errors.join(' ')); return; }
    setBusy(true);
    try {
      await addDoc(collection(db, 'bulletinPosts'), r.record);
      setTitle(''); setBody(''); setOpen(false);
      if (onPosted) onPosted();
    } catch (e) { setErr('Not posted: ' + (e && e.code ? e.code : e.message)); }
    setBusy(false);
  };
  if (!open) {
    return <button className="btn btn-primary btn-sm" style={{ marginBottom: 12 }} onClick={() => setOpen(true)}>Post a notice</button>;
  }
  return (
    <div style={{ background: '#0D1B2A', border: '1px solid ' + C.line, borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
      <input aria-label="Title" className="form-input" placeholder="Title" value={title} maxLength={TITLE_MAX} onChange={e => setTitle(e.target.value)} style={{ width: '100%', marginBottom: 6 }} />
      <textarea aria-label="The notice" className="form-input" placeholder="The notice" value={body} maxLength={BODY_MAX} rows={6} onChange={e => setBody(e.target.value)} style={{ width: '100%' }} />
      <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={post}>Post to {board.id}</button>
        <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
      {err ? <div style={{ color: C.bad, fontSize: 12, marginTop: 6 }}>{err}</div> : null}
    </div>
  );
}

export default function BulletinBoards({ user }) {
  const boards = useMemo(() => boardsForUser(user), [user]);
  const [boardIdx, setBoardIdx] = useState(0);
  const [posts, setPosts] = useState([]);
  const [showRemoved, setShowRemoved] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const board = boards[boardIdx] || null;

  useEffect(() => {
    if (!board) { setPosts([]); return undefined; }
    setLoadErr('');
    const q = query(collection(db, 'bulletinPosts'), where('boardId', '==', board.id));
    const unsub = onSnapshot(q,
      snap => setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      e => setLoadErr('This board could not be read: ' + (e && e.code ? e.code : e.message)));
    return unsub;
  }, [board && board.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!boards.length) {
    return (
      <div style={{ padding: 24, color: C.dim }}>
        <UnderConstruction note="No board is addressed to your account yet." />
        <h1 style={{ color: C.text, margin: 0 }}>Bulletin boards</h1>
        <p>No board is addressed to your account yet. A board belongs to a committee or to a local, and
           yours is set when your office is seated.</p>
      </div>
    );
  }

  const onBoard = postsOnBoard(posts, board);
  const removed = withdrawnOnBoard(posts, board);

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ color: C.text, marginBottom: 4 }}>
        Bulletin boards
        <RulingRef user={user} n={173} why="The General Chairperson posts to the committee board and may edit or remove anything on any board in the committee. A Local Chairperson posts to his own local board and manages his own posts. Anyone may copy a notice." />
      </h2>
      <div style={{ color: C.dim, fontSize: 12, marginBottom: 14 }}>
        {board && board.kind === 'committee'
          ? 'The committee board. The General Chairperson posts here.'
          : 'A local board. Its Local Chairperson posts what matters to this local’s members; the General Chairperson reaches it too.'}
      </div>

      {boards.length > 1 ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {boards.map((b, i) => (
            <button key={b.id} className={'btn btn-sm ' + (i === boardIdx ? 'btn-primary' : 'btn-secondary')} onClick={() => setBoardIdx(i)}>
              {b.id}
            </button>
          ))}
        </div>
      ) : null}

      {board ? <Composer board={board} user={user} /> : null}

      {loadErr ? <div style={{ color: C.bad, fontSize: 12, marginBottom: 10 }}>{loadErr}</div> : null}

      {onBoard.length === 0 && !loadErr ? (
        <div style={{ color: C.dim, fontSize: 13 }}>Nothing is posted on this board.</div>
      ) : null}

      {onBoard.map(p => <PostCard key={p.id} post={p} board={board} user={user} />)}

      {removed.length ? (
        <div style={{ marginTop: 18, borderTop: '1px solid ' + C.line, paddingTop: 10 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowRemoved(s => !s)}>
            {showRemoved ? 'Hide' : 'Show'} {removed.length} removed {removed.length === 1 ? 'notice' : 'notices'}
          </button>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>
            Removed notices are off the board and kept in the record.
            <RulingRef user={user} n={173} why="A notice taken off a board is never deleted. The removal is recorded with who made it and when." />
          </div>
          {showRemoved ? removed.map(p => (
            <div key={p.id} style={{ background: '#0A1420', border: '1px dashed ' + C.line, borderRadius: 8, padding: '10px 14px', marginTop: 8, opacity: 0.75 }}>
              <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{p.title}</div>
              <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
                Posted by {p.postedByName}{p.postedAt ? ' on ' + String(p.postedAt).slice(0, 10) : ''} ·
                {' '}removed by {p.withdrawnByName || 'unknown'}{p.withdrawnAt ? ' on ' + String(p.withdrawnAt).slice(0, 10) : ''}
              </div>
              {p.withdrawnReason ? <div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>{p.withdrawnReason}</div> : null}
            </div>
          )) : null}
        </div>
      ) : null}
    </div>
  );
}
